import { actions, definitions } from '@luxuryescapes/contract-svc-agent';

export type List<T> = {
  total: number;
  rows: Array<T>;
};

export type Eoi = definitions['eoiObject'];

export type EoiStatusValue = 'pending' | 'approved' | 'onboarded' | 'rejected' | 'offboarded';

export type AgentHubBooking = definitions['bookingSchema'];

export type AgentHubInvoiceDetails =
  actions['getBookingInvoiceDetails']['responses'][200]['content']['application/json']['result'];

export type EoiRegionCode = definitions['eoiObject']['regionCode'];

export type ListEoiParams = actions['eoiList']['parameters']['query'];

type ListUsersQueryGenerated = actions['getUsers']['parameters']['query'];

export type ListUserParams = ListUsersQueryGenerated & {
  affiliationId?: string;
  search?: string;
  order?: string;
};

export type EditUserParams = actions['updateUser']['requestBody']['content']['application/json'];

export type MoveUserAgencyParams = {
  targetAgencyId: string;
  deleteOldAgencyIfEmpty: boolean;
};

export type PreviewMoveUserAgencyParams = {
  targetAgencyId: string;
};

export type PreviewMoveUserAgencyResponse = {
  oldAgencyId: string;
  oldAgencyName: string;
  oldAgencyWillBeEmpty: boolean;
};

export type ListAgencyParams = actions['agencyList']['parameters']['query'];

export type ListAffiliationsParams = actions['affiliationList']['parameters']['query'];

export type Agency = definitions['agencySchema'];

export type Affiliation = definitions['maskedAffiliation'];
export type AffiliationWithRules = definitions['affiliationSchema'];

export type CreateAffiliationParams = actions['affiliationCreate']['requestBody']['content']['application/json'];

export type Agent = definitions['userSimpleSchema'];

export type GetAgenciesByIdResponse = actions['agencyListByIds']['responses']['200']['content']['application/json'];

export type MappedAgent = {
  id: string;
  name: string;
  phone: string;
  email: string;
  affiliation: string;
  customerId: string;
  agencyId: string;
  eoiSubmissionId: string;
  agencyName: string;
  status: string;
  verifiedAt: string;
  isAgencyAdmin?: boolean;
  isAffiliationAdmin?: boolean;
};

export type AvailableAffiliations = Array<definitions['maskedAffiliation']>;

export type CommissionFlightCarrierRule = { code: string; name: string } | string;
export type CommissionValueLimit = definitions['commissionResponse'] extends {
  commissionValueLimits?: Array<infer T>;
}
  ? T
  : never;

export type CommissionGroupOperator = 'AND' | 'OR';
export type CommissionConditionOperator = 'eq' | 'gt' | 'lt' | 'gte' | 'lte' | 'neq';

type CommissionInclusionFields = {
  includesTaxesAndFees?: boolean;
  includesMargin?: boolean;
};

export type Commission = Omit<definitions['commissionResponse'], 'commissionGroups'> & {
  commissionGroups: Array<{
    id: string;
    operator: CommissionGroupOperator;
    conditions: Array<{
      id: string;
      operator: CommissionConditionOperator;
      value: string;
      type: string;
    }>;
  }>;
  rules?: Record<string, Array<string | number>>;
};

export type CommissionCondition = {
  id: string;
  operator: CommissionConditionOperator;
  value: string;
  type: string;
};

export type CommissionGroup = {
  id: string;
  operator: CommissionGroupOperator;
  conditions: Array<CommissionCondition>;
};

export type Tier = NonNullable<definitions['agencySchema']['tier']>;

export type AgentCommission = {
  commissionPercentage: number;
  commission: number;
};

export type GetAgenciesResponse = actions['agencyList']['responses']['200']['content']['application/json'];
export type GetAgentsResponse = actions['getUsers']['responses']['200']['content']['application/json'];

export type ListAffiliationsResponse = actions['affiliationList']['responses']['200']['content']['application/json'];
export type GetAffiliationResponse = actions['affiliationGet']['responses']['200']['content']['application/json'];
export type UpdateAffiliationResponse = actions['affiliationUpdate']['responses']['200']['content']['application/json'];
export type UpdateAffiliationParams = actions['affiliationUpdate']['requestBody']['content']['application/json'];
export type AffiliationRules = NonNullable<AffiliationWithRules['rules']>;

export type CreateCommissionParams = actions['createCommission']['requestBody']['content']['application/json'] &
  CommissionInclusionFields;
export type CreateCommissionResponse = actions['createCommission']['responses']['200']['content']['application/json'];

export type getCommissionsResponse = actions['getCommissionList']['responses']['200']['content']['application/json'];
export type getCommissionsParams = actions['getCommissionList']['parameters']['query'] & {
  type?: 'base' | 'additional' | 'incentive';
  conditions?: string;
};
export type EditCommissionParams = actions['updateCommission']['requestBody']['content']['application/json'] &
  CommissionInclusionFields;

export type ListQuotedCommissionsParams = {
  page?: string;
  pageSize?: string;
  transactionId?: string;
  userId?: string;
  orderId?: string;
};

export type QuotedCommission = {
  id: string;
  userId: string;
  cart: Record<string, unknown>;
  itemsCommission: Record<string, unknown> | null;
  region: string;
  totalDiscount: number;
  commissionPercentage: number;
  used: boolean;
  expiresAt: string;
  promoCode: string | null;
  promoCodeId: string | null;
  createdAt: string;
  updatedAt: string;
  bookingId: string | null;
  orderTransactionKey: string | null;
};

export type QuotedCommissionBooking = {
  id: string;
  orderId: string;
  orderNumber: string;
  status: string;
  userId: string;
  createdAt: string;
};

export type QuotedCommissionListRow = {
  quotedCommission: QuotedCommission;
  booking: QuotedCommissionBooking | null;
};

export type ListQuotedCommissionsResponse = List<QuotedCommissionListRow>;

export type EditAgencyParams = actions['agencyUpdate']['requestBody']['content']['application/json'];
export type EditAgencyResponse = definitions['agencySchema'];

export type UpdateEoiDetailsParams = actions['updateEoiDetails']['requestBody']['content']['application/json'];

export type CreateEoiParams = actions['eoiCreate']['requestBody']['content']['application/json'];

export type CreateDiscountVoucherParams =
  actions['createDiscountVoucher']['requestBody']['content']['application/json'];
export type CreateDiscountVoucherResponse =
  actions['createDiscountVoucher']['responses']['202']['content']['application/json'];

export type GetAvailableAffiliationsResponse =
  actions['affiliationListAvailable']['responses']['200']['content']['application/json'];

export type ListUserEmailLogsParams = actions['userEmailLogs']['parameters']['query'];

export type CreateRegistrationTokenParams =
  actions['createRegistrationToken']['requestBody']['content']['application/json'];

export type CreateRegistrationTokenResponse =
  actions['createRegistrationToken']['responses']['200']['content']['application/json']['result'];

export type UserEmailLogsResponse =
  actions['userEmailLogs']['responses']['200']['content']['application/json']['result'];

export const EoiStatus = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
  Onboarded: 'onboarded',
  Offboarded: 'offboarded',
};

export type Reward = definitions['rewardResponse'];
export type RewardType = Reward['rewardType'];
export type RewardTargetAudience = Reward['targetAudience'];
export type RewardCondition = Reward['conditions'][number];
export type RewardConditionType = RewardCondition['type'];
export type CreateRewardParams = actions['createReward']['requestBody']['content']['application/json'];
export type UpdateRewardParams = actions['updateReward']['requestBody']['content']['application/json'];
export type RewardConditionPayload = CreateRewardParams['conditions'][number];
export type RewardConditionOperator = RewardConditionPayload['operator'];
export type GetRewardsParams = actions['getRewardList']['parameters']['query'];
export type RewardSubjectType = NonNullable<GetRewardsParams['subjectType']>;
export type GetRewardsResponse = actions['getRewardList']['responses']['200']['content']['application/json'];
export type GetRewardResponse = actions['getReward']['responses']['200']['content']['application/json'];
export type CreateRewardResponse = actions['createReward']['responses']['201']['content']['application/json'];
export type UpdateRewardResponse = actions['updateReward']['responses']['200']['content']['application/json'];

export type GetAchievementsParams = actions['getAchievementList']['parameters']['query'];
export type GetAchievementsResponse = actions['getAchievementList']['responses']['200']['content']['application/json'];
export type AchievementListRow = GetAchievementsResponse['result']['rows'][number];
export type GetAchievementResponse = actions['getAchievement']['responses']['200']['content']['application/json'];
export type AchievementDetail = GetAchievementResponse['result'];
